/**
 * Midgard World Data - Hierarchical Location System
 * 
 * Structure: REALM → REGIONS → SETTLEMENTS → LOCATIONS
 * 
 * Each object has:
 * - id: unique identifier
 * - parentId: reference to parent object
 * - type: category of the location
 * - position: { x, y } percentage for map placement
 * 
 * PHASE 3: Open-World Coordinate System
 * =====================================
 * - worldId: 'midgard' for continuous outdoor space
 * - worldPosition: { x, y, z } 3D coordinates in world space
 * - regionId: geographic region for LOD/streaming
 * - sceneId: isolated interiors only (e.g., 'golden-mead-hall')
 * 
 * Chunk System:
 * - 64x64 unit chunks (configurable)
 * - 3x3 active chunk grid around player
 * - Data-driven terrain/object loading
 */

import { LOCATION_TYPES } from './worldData';

// ============================================
// WORLD COORDINATE SYSTEM CONFIGURATION
// ============================================
export const WORLD_CONFIG = {
  // Main world identifier
  worldId: 'midgard',
  
  // Chunk streaming settings
  chunkSize: 64,           // Units per chunk side
  activeChunkRadius: 2,    // 5x5 grid for longer visibility (was 1 for 3x3)
  loadDistance: 150,       // Distance to start loading chunks
  unloadDistance: 200,     // Distance to unload chunks
  
  // Multiplayer spatial settings
  playerRelevanceRadius: 100,   // Broadcast position to players within this range
  playerCullRadius: 150,        // Stop rendering players beyond this
  
  // Performance tuning
  maxVisiblePlayers: 30,
  positionUpdateThrottle: 66,   // ~15Hz
  
  // Thornhaven terrain (for ChunkStreamer overlap calculation)
  thornhavenTerrainSize: 150,
};

// ============================================
// BIOME DEFINITIONS
// Gradual terrain color transitions based on Z coordinate
// Negative Z is NORTH (colder)
// ============================================
export const BIOMES = {
  'central-plains': {
    id: 'central-plains',
    name: 'Central Plains',
    zRange: [Infinity, -200],  // z > -200
    terrainColor: '#3D5C1F',   // Green grass
    treeTypes: ['pine', 'oak'],
    rockColor: '#6b7280',
  },
  'northern-woodland': {
    id: 'northern-woodland',
    name: 'Northern Woodland',
    zRange: [-200, -350],
    terrainColor: '#2D4A18',   // Darker green forest
    treeTypes: ['pine', 'pine'],
    rockColor: '#5a6370',
  },
  'rocky-highlands': {
    id: 'rocky-highlands',
    name: 'Rocky Highlands',
    zRange: [-350, -480],
    terrainColor: '#4A5D4A',   // Grey-green rocky
    treeTypes: ['pine'],
    rockColor: '#505860',
  },
  'snow-transition': {
    id: 'snow-transition',
    name: 'Snow Transition',
    zRange: [-480, -560],
    terrainColor: '#6A7A6A',   // Grey with hints of snow
    treeTypes: ['snow-pine'],
    rockColor: '#707a80',
  },
  'northern-frost': {
    id: 'northern-frost',
    name: 'Northern Frost',
    zRange: [-560, -Infinity], // z < -560
    terrainColor: '#8A9A9A',   // Snowy grey-white
    treeTypes: ['snow-pine'],
    rockColor: '#8090a0',
  },
};

// Helper: Get biome for a Z coordinate
export const getBiomeForZ = (z) => {
  for (const biome of Object.values(BIOMES)) {
    const [maxZ, minZ] = biome.zRange;
    if (z <= maxZ && z > minZ) {
      return biome;
    }
  }
  return BIOMES['central-plains'];
};

// Helper: Get interpolated terrain color for smooth transitions
export const getTerrainColorForZ = (z) => {
  const biome = getBiomeForZ(z);
  return biome.terrainColor;
};

// ============================================
// WORLD SPAWN POINTS (3D Coordinates)
// ============================================
export const WORLD_SPAWN_POINTS = {
  // Thornhaven - Main town spawn (center of town)
  'thornhaven-gate': {
    worldId: 'midgard',
    regionId: 'central-plains',
    worldPosition: { x: 0, y: 1, z: 25 },
    name: 'Thornhaven Gate',
    description: 'The main entrance to Thornhaven',
  },
  
  // Thornhaven - Mead Hall exit
  'thornhaven-mead-hall-exit': {
    worldId: 'midgard',
    regionId: 'central-plains',
    worldPosition: { x: 10, y: 1, z: 12 },
    name: 'Golden Mead Hall Entrance',
    description: 'Outside the Golden Mead Hall',
  },
  
  // Standing Stones - Wilderness landmark (~190 units north)
  'standing-stones': {
    worldId: 'midgard',
    regionId: 'central-plains',
    worldPosition: { x: 0, y: 1, z: -190 },
    name: 'Standing Stones',
    description: 'Ancient Norse standing stones in the wilderness',
  },
  
  // Fast travel default (town center)
  'thornhaven-fast-travel': {
    worldId: 'midgard',
    regionId: 'central-plains',
    worldPosition: { x: 0, y: 1, z: 25 },
    name: 'Thornhaven',
    description: 'Fast travel arrival point',
  },
  
  // ============================================
  // FROSTHOLM SPAWN POINTS (Expansion 1)
  // ============================================
  
  // Frostholm - Main gate entrance
  'frostholm-gate': {
    worldId: 'midgard',
    regionId: 'northern-frost',
    worldPosition: { x: 0, y: 1, z: -590 },
    name: 'Frostholm Gate',
    description: 'The southern entrance to Frostholm',
  },
  
  // Frostholm - Fast travel spawn (town center)
  'frostholm-fast-travel': {
    worldId: 'midgard',
    regionId: 'northern-frost',
    worldPosition: { x: 0, y: 1, z: -620 },
    name: 'Frostholm',
    description: 'Fast travel arrival point in Frostholm',
  },
  
  // ============================================
  // LANDMARK SPAWN POINTS (Road to Frostholm)
  // ============================================
  
  'ruined-watchtower': {
    worldId: 'midgard',
    regionId: 'northern-woodland',
    worldPosition: { x: 15, y: 1, z: -290 },
    name: 'Ruined Watchtower',
    description: 'An ancient watchtower overlooking the northern road',
  },
  
  'river-crossing': {
    worldId: 'midgard',
    regionId: 'rocky-highlands',
    worldPosition: { x: 0, y: 1, z: -400 },
    name: 'River Crossing',
    description: 'A stone bridge over the northern river',
  },
  
  'abandoned-camp': {
    worldId: 'midgard',
    regionId: 'rocky-highlands',
    worldPosition: { x: -10, y: 1, z: -470 },
    name: 'Abandoned Camp',
    description: 'Remnants of a traveler camp',
  },
};

// ============================================
// INTERIOR SCENE MAPPINGS
// Door positions link interiors to world coordinates
// ============================================
export const INTERIOR_SCENES = {
  'golden-mead-hall': {
    sceneId: 'golden-mead-hall',
    name: 'The Golden Mead Hall',
    // When exiting, return to this world position
    exitSpawn: 'thornhaven-mead-hall-exit',
    // Interior spawn points (local coordinates)
    spawnPoints: {
      entrance: { x: 0, y: 1, z: 6 },
      fastTravel: { x: 0, y: 1, z: 0 },
    },
  },
};

// ============================================
// CHUNK DEFINITIONS
// Each chunk contains terrain and object data
// Chunk coordinate system: chunk(x,z) is centered at (x*64, z*64)
// Negative Z is NORTH (toward Frostholm)
// 
// ROUTE: Thornhaven (z=0) → Standing Stones (z=-190) → Frostholm (z=-620)
// Total distance: ~620 units (~1.5-2 minute walk)
// ============================================
export const WORLD_CHUNKS = {
  // ========== THORNHAVEN AREA (z > -96) ==========
  
  // Chunk at origin (Thornhaven area) - gate marks the town exit
  '0,0': {
    chunkX: 0,
    chunkZ: 0,
    regionId: 'central-plains',
    biome: 'central-plains',
    hasSettlement: true,
    settlementId: 'thornhaven',
    objects: [
      { type: 'gate', position: { x: 0, z: -30 }, rotation: 0 },
      { type: 'road', from: { x: 0, z: -30 }, to: { x: 0, z: -64 } },
    ],
  },
  
  // Chunk north of Thornhaven (wilderness begins)
  '0,-1': {
    chunkX: 0,
    chunkZ: -1,
    regionId: 'central-plains',
    biome: 'central-plains',
    hasRoad: true,
    objects: [
      { type: 'road', from: { x: 0, z: -64 }, to: { x: 0, z: -96 } },
      { type: 'tree', position: { x: 12, z: -70 } },
      { type: 'tree', position: { x: -15, z: -80 } },
      { type: 'tree', position: { x: 18, z: -90 } },
      { type: 'rock', position: { x: -10, z: -75 } },
    ],
  },
  
  // ========== CENTRAL PLAINS (z: -96 to -224) ==========
  
  // Deeper wilderness toward Standing Stones
  '0,-2': {
    chunkX: 0,
    chunkZ: -2,
    regionId: 'central-plains',
    biome: 'central-plains',
    hasRoad: true,
    objects: [
      { type: 'road', from: { x: 0, z: -96 }, to: { x: 0, z: -160 } },
      { type: 'tree', position: { x: 15, z: -110 } },
      { type: 'tree', position: { x: -20, z: -125 } },
      { type: 'tree', position: { x: 22, z: -140 } },
      { type: 'rock', position: { x: 25, z: -130 } },
      { type: 'rock', position: { x: -18, z: -150 } },
    ],
  },
  
  // Standing Stones landmark - POSITIONED TO THE SIDE OF THE ROAD
  '0,-3': {
    chunkX: 0,
    chunkZ: -3,
    regionId: 'central-plains',
    biome: 'central-plains',
    hasRoad: true,
    hasLandmark: true,
    objects: [
      // Road goes STRAIGHT through - Standing Stones are OFF to the west side
      { type: 'road', from: { x: 0, z: -160 }, to: { x: 0, z: -224 } },
      // Standing Stones circle - offset 15 units WEST of road (x=-15) so road is clear
      { type: 'standing-stones', position: { x: -15, z: -190 } },
      // Trees scattered around
      { type: 'tree', position: { x: 15, z: -175 } },
      { type: 'tree', position: { x: 20, z: -195 } },
      { type: 'tree', position: { x: 12, z: -210 } },
    ],
  },
  
  // ========== NORTHERN WOODLAND (z: -224 to -352) ==========
  
  // Road continues north, forest gets denser and darker
  '0,-4': {
    chunkX: 0,
    chunkZ: -4,
    regionId: 'northern-woodland',
    biome: 'northern-woodland',
    hasRoad: true,
    objects: [
      { type: 'road', from: { x: 0, z: -224 }, to: { x: 0, z: -288 } },
      // Denser forest
      { type: 'tree', position: { x: 8, z: -235 } },
      { type: 'tree', position: { x: -12, z: -245 } },
      { type: 'tree', position: { x: 15, z: -260 } },
      { type: 'tree', position: { x: -18, z: -270 } },
      { type: 'tree', position: { x: 22, z: -280 } },
      { type: 'rock', position: { x: -8, z: -255 } },
    ],
  },
  
  // LANDMARK 1: Ruined Watchtower - off to the EAST side of road
  '0,-5': {
    chunkX: 0,
    chunkZ: -5,
    regionId: 'northern-woodland',
    biome: 'northern-woodland',
    hasRoad: true,
    hasLandmark: true,
    objects: [
      // Road continues straight
      { type: 'road', from: { x: 0, z: -288 }, to: { x: 0, z: -352 } },
      // Ruined Watchtower - offset 18 units EAST of road
      { type: 'ruined-watchtower', position: { x: 18, z: -320 } },
      // Forest around
      { type: 'tree', position: { x: -15, z: -295 } },
      { type: 'tree', position: { x: -20, z: -330 } },
      { type: 'tree', position: { x: -12, z: -345 } },
      { type: 'rock', position: { x: -10, z: -315 } },
      { type: 'rock', position: { x: 28, z: -305 } },
    ],
  },
  
  // ========== ROCKY HIGHLANDS (z: -352 to -480) ==========
  
  // Transition to rockier terrain
  '0,-6': {
    chunkX: 0,
    chunkZ: -6,
    regionId: 'rocky-highlands',
    biome: 'rocky-highlands',
    hasRoad: true,
    objects: [
      { type: 'road', from: { x: 0, z: -352 }, to: { x: 0, z: -416 } },
      // More rocks, fewer trees
      { type: 'rock', position: { x: 12, z: -365 } },
      { type: 'rock', position: { x: -15, z: -380 } },
      { type: 'rock', position: { x: 20, z: -395 } },
      { type: 'tree', position: { x: -22, z: -370 } },
      { type: 'tree', position: { x: 18, z: -410 } },
    ],
  },
  
  // LANDMARK 2: River Crossing / Stone Bridge
  '0,-7': {
    chunkX: 0,
    chunkZ: -7,
    regionId: 'rocky-highlands',
    biome: 'rocky-highlands',
    hasRoad: true,
    hasLandmark: true,
    objects: [
      // Road leads to and through bridge
      { type: 'road', from: { x: 0, z: -416 }, to: { x: 0, z: -420 } },
      // Stone bridge over river - road goes through it
      { type: 'stone-bridge', position: { x: 0, z: -430 } },
      // Road continues after bridge
      { type: 'road', from: { x: 0, z: -440 }, to: { x: 0, z: -480 } },
      // River (visual only, runs east-west)
      { type: 'river', from: { x: -40, z: -430 }, to: { x: 40, z: -430 } },
      // Rocks near river
      { type: 'rock', position: { x: -18, z: -420 } },
      { type: 'rock', position: { x: 22, z: -440 } },
      { type: 'rock', position: { x: -25, z: -450 } },
    ],
  },
  
  // LANDMARK 3: Abandoned Camp - off to the WEST side of road
  '0,-8': {
    chunkX: 0,
    chunkZ: -8,
    regionId: 'rocky-highlands',
    biome: 'rocky-highlands',
    hasRoad: true,
    hasLandmark: true,
    objects: [
      // Road continues straight
      { type: 'road', from: { x: 0, z: -480 }, to: { x: 0, z: -512 } },
      // Abandoned camp - offset 12 units WEST of road
      { type: 'abandoned-camp', position: { x: -12, z: -495 } },
      // Scattered rocks
      { type: 'rock', position: { x: 15, z: -485 } },
      { type: 'rock', position: { x: 20, z: -505 } },
      { type: 'tree', position: { x: -25, z: -480 } },
      { type: 'tree', position: { x: 25, z: -510 } },
    ],
  },
  
  // ========== SNOW TRANSITION (z: -480 to -560) ==========
  
  // Getting colder, some snow patches
  '0,-9': {
    chunkX: 0,
    chunkZ: -9,
    regionId: 'snow-transition',
    biome: 'snow-transition',
    hasRoad: true,
    objects: [
      { type: 'road', from: { x: 0, z: -512 }, to: { x: 0, z: -576 } },
      // Sparse snow-covered pines
      { type: 'snow-tree', position: { x: 15, z: -520 } },
      { type: 'snow-tree', position: { x: -18, z: -540 } },
      { type: 'snow-tree', position: { x: 22, z: -560 } },
      // Snowy rocks
      { type: 'snow-rock', position: { x: -12, z: -530 } },
      { type: 'snow-rock', position: { x: 18, z: -550 } },
      // Rune stone (small landmark) - off to the side
      { type: 'rune-stone', position: { x: -8, z: -555 } },
    ],
  },
  
  // ========== NORTHERN FROST / FROSTHOLM (z < -560) ==========
  
  // Frostholm outskirts
  '0,-10': {
    chunkX: 0,
    chunkZ: -10,
    regionId: 'northern-frost',
    biome: 'northern-frost',
    hasRoad: true,
    objects: [
      { type: 'road', from: { x: 0, z: -576 }, to: { x: 0, z: -590 } },
      // Frostholm southern gate
      { type: 'frost-gate', position: { x: 0, z: -590 }, rotation: 0 },
      // Road continues into settlement
      { type: 'road', from: { x: 0, z: -590 }, to: { x: 0, z: -640 } },
      // Outskirt trees
      { type: 'snow-tree', position: { x: 20, z: -580 } },
      { type: 'snow-tree', position: { x: -22, z: -585 } },
    ],
  },
  
  // Frostholm settlement center
  '0,-11': {
    chunkX: 0,
    chunkZ: -11,
    regionId: 'northern-frost',
    biome: 'northern-frost',
    hasSettlement: true,
    hasRoad: true,
    settlementId: 'frostholm',
    objects: [
      // Main road through town
      { type: 'road', from: { x: 0, z: -640 }, to: { x: 0, z: -704 } },
      // Side roads
      { type: 'road', from: { x: 0, z: -660 }, to: { x: 30, z: -660 } },
      { type: 'road', from: { x: 0, z: -660 }, to: { x: -30, z: -660 } },
      // Frostholm buildings - all offset from road
      { type: 'frost-longhouse', position: { x: 20, z: -650 }, rotation: Math.PI / 2 },
      { type: 'frost-longhouse', position: { x: -20, z: -650 }, rotation: -Math.PI / 2 },
      { type: 'frost-hall', position: { x: 0, z: -680 }, rotation: 0 },
      { type: 'frost-house', position: { x: 25, z: -670 }, rotation: Math.PI / 4 },
      { type: 'frost-house', position: { x: -25, z: -670 }, rotation: -Math.PI / 4 },
      { type: 'frost-house', position: { x: 30, z: -695 }, rotation: 0 },
      { type: 'frost-house', position: { x: -30, z: -695 }, rotation: 0 },
      // Central fire pit - in the plaza area, not blocking main road
      { type: 'fire-pit', position: { x: 8, z: -660 } },
      // Snow-covered trees around settlement
      { type: 'snow-tree', position: { x: 35, z: -645 } },
      { type: 'snow-tree', position: { x: -35, z: -645 } },
      { type: 'snow-tree', position: { x: 38, z: -690 } },
      { type: 'snow-tree', position: { x: -38, z: -690 } },
    ],
  },
  
  // Frostholm northern area (future expansion)
  '0,-12': {
    chunkX: 0,
    chunkZ: -12,
    regionId: 'northern-frost',
    biome: 'northern-frost',
    hasRoad: true,
    objects: [
      // Road continues north (future expansion)
      { type: 'road', from: { x: 0, z: -704 }, to: { x: 0, z: -750 } },
      // Northern trees and rocks
      { type: 'snow-tree', position: { x: 15, z: -720 } },
      { type: 'snow-tree', position: { x: -18, z: -735 } },
      { type: 'snow-rock', position: { x: 22, z: -745 } },
      { type: 'snow-rock', position: { x: -12, z: -760 } },
    ],
  },
};

// ============================================
// HELPER: Get chunk key from world position
// ============================================
export const getChunkKey = (worldX, worldZ) => {
  const chunkX = Math.floor(worldX / WORLD_CONFIG.chunkSize);
  const chunkZ = Math.floor(worldZ / WORLD_CONFIG.chunkSize);
  return `${chunkX},${chunkZ}`;
};

// ============================================
// HELPER: Get all chunks in radius around position
// ============================================
export const getActiveChunks = (worldX, worldZ) => {
  const centerChunkX = Math.floor(worldX / WORLD_CONFIG.chunkSize);
  const centerChunkZ = Math.floor(worldZ / WORLD_CONFIG.chunkSize);
  const radius = WORLD_CONFIG.activeChunkRadius;
  
  const chunks = [];
  for (let dx = -radius; dx <= radius; dx++) {
    for (let dz = -radius; dz <= radius; dz++) {
      const key = `${centerChunkX + dx},${centerChunkZ + dz}`;
      const chunkData = WORLD_CHUNKS[key];
      if (chunkData) {
        chunks.push({ key, ...chunkData });
      } else {
        // Generate empty chunk data for unmapped areas
        chunks.push({
          key,
          chunkX: centerChunkX + dx,
          chunkZ: centerChunkZ + dz,
          regionId: 'central-plains',
          terrain: 'grass',
          objects: [],
        });
      }
    }
  }
  return chunks;
};

// ============================================
// HELPER: Check if position is in an interior
// ============================================
export const isInInterior = (sceneId) => {
  return sceneId && INTERIOR_SCENES[sceneId] !== undefined;
};

// ============================================
// HELPER: Get spawn point data
// ============================================
export const getWorldSpawnPoint = (spawnId) => {
  return WORLD_SPAWN_POINTS[spawnId] || WORLD_SPAWN_POINTS['thornhaven-gate'];
};

// ============================================
// MIDGARD REGIONS (Geographic areas on the regional map)
// ============================================
export const MIDGARD_REGIONS = [
  {
    id: 'northern-highlands',
    realmId: 'midgard',
    name: 'Northern Highlands',
    type: 'mountain',
    description: 'Snow-capped mountains and deep fjords stretch across the northern reaches.',
    position: { x: 50, y: 12 },
    size: { width: 60, height: 20 },
    color: '#6B7280', // Gray for mountains
  },
  {
    id: 'eastern-coast',
    realmId: 'midgard',
    name: 'Eastern Coast',
    type: 'coast',
    description: 'Rocky coastline with fishing villages and ancient harbors.',
    position: { x: 85, y: 50 },
    size: { width: 15, height: 60 },
    color: '#3B82F6', // Blue for coast
  },
  {
    id: 'western-forests',
    realmId: 'midgard',
    name: 'Western Forests',
    type: 'forest',
    description: 'Dense ancient woodlands home to druids and mysterious creatures.',
    position: { x: 15, y: 45 },
    size: { width: 25, height: 40 },
    color: '#166534', // Dark green for forest
  },
  {
    id: 'central-plains',
    realmId: 'midgard',
    name: 'Central Plains',
    type: 'plains',
    description: 'Fertile farmlands and rolling hills, the heart of human civilization.',
    position: { x: 50, y: 50 },
    size: { width: 35, height: 35 },
    color: '#84CC16', // Green for plains
  },
  {
    id: 'southern-marshes',
    realmId: 'midgard',
    name: 'Southern Marshes',
    type: 'swamp',
    description: 'Misty wetlands and treacherous bogs, hiding ancient secrets.',
    position: { x: 40, y: 85 },
    size: { width: 40, height: 15 },
    color: '#4D7C0F', // Olive for marshes
  },
];

// ============================================
// MIDGARD SETTLEMENTS (Towns, villages, cities)
// ============================================
export const MIDGARD_SETTLEMENTS = [
  {
    id: 'thornhaven',
    realmId: 'midgard',
    name: 'Thornhaven',
    type: LOCATION_TYPES.TOWN,
    regionId: 'central-plains',
    description: 'A bustling market town at the crossroads of major trade routes. The social hub of Midgard.',
    position: { x: 50, y: 45 },
    population: 'Large',
    unlocked: true,
    isStarterTown: true, // Main town for initial development
    theme: {
      primary: '#D97706', // Amber
      secondary: '#F59E0B',
      bgGradient: 'from-amber-900/80 via-orange-900/60 to-yellow-900/80',
    },
  },
  {
    id: 'frostholm',
    realmId: 'midgard',
    name: 'Frostholm',
    type: LOCATION_TYPES.VILLAGE,
    regionId: 'northern-highlands',
    description: 'A hardy mountain village known for its skilled blacksmiths and sturdy mead halls.',
    position: { x: 45, y: 18 },
    population: 'Small',
    unlocked: true,
    theme: {
      primary: '#60A5FA', // Blue
      secondary: '#93C5FD',
      bgGradient: 'from-blue-900/80 via-slate-800/60 to-gray-900/80',
    },
  },
  {
    id: 'stonewatch',
    realmId: 'midgard',
    name: 'Stonewatch',
    type: LOCATION_TYPES.TOWN,
    regionId: 'eastern-coast',
    description: 'A fortified coastal stronghold watching over the eastern shores of Midgard.',
    position: { x: 82, y: 55 },
    population: 'Medium',
    unlocked: false,
    theme: {
      primary: '#0EA5E9', // Sky blue
      secondary: '#38BDF8',
      bgGradient: 'from-sky-900/80 via-blue-900/60 to-slate-900/80',
    },
  },
  {
    id: 'ravenmoor',
    realmId: 'midgard',
    name: 'Ravenmoor',
    type: LOCATION_TYPES.VILLAGE,
    regionId: 'western-forests',
    description: 'A mysterious village on the edge of the western forests, known for its wise elders.',
    position: { x: 18, y: 50 },
    population: 'Small',
    unlocked: false,
    theme: {
      primary: '#22C55E', // Green
      secondary: '#4ADE80',
      bgGradient: 'from-green-900/80 via-emerald-900/60 to-teal-900/80',
    },
  },
  {
    id: 'mirefen',
    realmId: 'midgard',
    name: 'Mirefen',
    type: LOCATION_TYPES.VILLAGE,
    regionId: 'southern-marshes',
    description: 'A mysterious outpost on the edge of the marshes, home to herbalists and swamp guides.',
    position: { x: 45, y: 82 },
    population: 'Small',
    unlocked: false,
    theme: {
      primary: '#84CC16', // Lime
      secondary: '#A3E635',
      bgGradient: 'from-lime-900/80 via-green-900/60 to-emerald-950/80',
    },
  },
];

// ============================================
// SETTLEMENT LOCATIONS (Places within settlements)
// Each location has settlementId for explicit parent relationship
// ============================================
export const SETTLEMENT_LOCATIONS = {
  // Thornhaven - The main starter town
  thornhaven: [
    {
      id: 'thornhaven-square',
      settlementId: 'thornhaven',
      name: 'Town Square',
      type: 'gathering',
      description: 'The heart of Thornhaven where travelers gather, merchants hawk their wares, and news spreads like wildfire.',
      position: { x: 50, y: 45 },
      unlocked: true,
      capacity: 50,
    },
    {
      id: 'thornhaven-tavern',
      settlementId: 'thornhaven',
      name: 'The Golden Mead Hall',
      type: LOCATION_TYPES.TAVERN,
      description: 'A warm and welcoming tavern famous for its honey mead and roaring fireplace. A favorite gathering spot for adventurers.',
      position: { x: 35, y: 35 },
      unlocked: true,
      capacity: 25,
    },
    {
      id: 'thornhaven-market',
      settlementId: 'thornhaven',
      name: 'Merchant\'s Row',
      type: LOCATION_TYPES.SHOP,
      description: 'A bustling marketplace where traders sell everything from fresh produce to exotic artifacts.',
      position: { x: 65, y: 40 },
      unlocked: true,
      capacity: 40,
    },
    {
      id: 'thornhaven-smithy',
      settlementId: 'thornhaven',
      name: 'Iron Forge Smithy',
      type: LOCATION_TYPES.SHOP,
      description: 'The local blacksmith\'s workshop, filled with the ring of hammers and the glow of the forge.',
      position: { x: 70, y: 60 },
      unlocked: true,
      capacity: 10,
    },
    {
      id: 'thornhaven-residential',
      settlementId: 'thornhaven',
      name: 'Hearthstone District',
      type: 'residential',
      description: 'Quiet cobblestone streets lined with cozy homes and small gardens.',
      position: { x: 30, y: 55 },
      unlocked: true,
      capacity: 30,
    },
    {
      id: 'thornhaven-gate',
      settlementId: 'thornhaven',
      name: 'Northern Gate',
      type: LOCATION_TYPES.ROAD,
      description: 'The main entrance to Thornhaven, guarded by stone towers and iron-bound gates.',
      position: { x: 50, y: 15 },
      unlocked: true,
      capacity: 15,
    },
    {
      id: 'thornhaven-well',
      settlementId: 'thornhaven',
      name: 'Wishwell Plaza',
      type: LOCATION_TYPES.BUILDING,
      description: 'A peaceful plaza centered around an ancient well, said to grant wishes to the pure of heart.',
      position: { x: 55, y: 70 },
      unlocked: true,
      capacity: 20,
    },
    {
      id: 'thornhaven-grove',
      settlementId: 'thornhaven',
      name: 'Elder\'s Grove',
      type: LOCATION_TYPES.WILDERNESS,
      description: 'A small sacred grove on the outskirts of town where the village elders hold council.',
      position: { x: 20, y: 75 },
      unlocked: false,
      capacity: 15,
    },
  ],

  // Frostholm - Mountain village
  frostholm: [
    {
      id: 'frostholm-hall',
      settlementId: 'frostholm',
      name: 'Great Mead Hall',
      type: LOCATION_TYPES.TAVERN,
      description: 'A massive longhouse where warriors share tales of glory over flagons of strong mead.',
      position: { x: 50, y: 50 },
      unlocked: true,
      capacity: 30,
    },
    {
      id: 'frostholm-forge',
      settlementId: 'frostholm',
      name: 'Frostforge',
      type: LOCATION_TYPES.SHOP,
      description: 'Legendary smithy known for crafting weapons that never lose their edge in the cold.',
      position: { x: 70, y: 40 },
      unlocked: true,
      capacity: 8,
    },
    {
      id: 'frostholm-lookout',
      settlementId: 'frostholm',
      name: 'Eagle\'s Perch',
      type: LOCATION_TYPES.MOUNTAIN,
      description: 'A watchtower at the highest point of the village, offering views across the highlands.',
      position: { x: 30, y: 25 },
      unlocked: true,
      capacity: 5,
    },
    {
      id: 'frostholm-shrine',
      settlementId: 'frostholm',
      name: 'Shrine of the Frost',
      type: LOCATION_TYPES.BUILDING,
      description: 'An ancient shrine dedicated to the spirits of winter and the northern winds.',
      position: { x: 55, y: 75 },
      unlocked: false,
      capacity: 10,
    },
  ],

  // Ravensport - Harbor town (locked)
  ravensport: [
    {
      id: 'ravensport-docks',
      settlementId: 'ravensport',
      name: 'Harbor Docks',
      type: LOCATION_TYPES.BUILDING,
      description: 'Busy docks where ships from across the known world come to trade.',
      position: { x: 50, y: 30 },
      unlocked: false,
      capacity: 50,
    },
    {
      id: 'ravensport-market',
      settlementId: 'ravensport',
      name: 'Foreign Quarter',
      type: LOCATION_TYPES.SHOP,
      description: 'An exotic marketplace filled with goods from distant lands.',
      position: { x: 50, y: 60 },
      unlocked: false,
      capacity: 35,
    },
  ],

  // Elderwood - Forest village (locked)
  elderwood: [
    {
      id: 'elderwood-circle',
      settlementId: 'elderwood',
      name: 'Druid\'s Circle',
      type: LOCATION_TYPES.WILDERNESS,
      description: 'Ancient standing stones where druids perform their sacred rituals.',
      position: { x: 50, y: 50 },
      unlocked: false,
      capacity: 20,
    },
  ],

  // Bogwatch - Marsh outpost (locked)
  bogwatch: [
    {
      id: 'bogwatch-tower',
      settlementId: 'bogwatch',
      name: 'Watchtower',
      type: LOCATION_TYPES.BUILDING,
      description: 'A lonely tower watching over the treacherous marshlands.',
      position: { x: 50, y: 50 },
      unlocked: false,
      capacity: 8,
    },
  ],
};

// ============================================
// HELPER FUNCTIONS
// ============================================

// Get all regions in Midgard
export const getMidgardRegions = () => MIDGARD_REGIONS;

// Get a specific region by ID
export const getMidgardRegion = (regionId) => MIDGARD_REGIONS.find(r => r.id === regionId);

// Get all settlements in Midgard
export const getMidgardSettlements = () => MIDGARD_SETTLEMENTS;

// Get settlements in a specific region
export const getSettlementsByRegion = (regionId) => 
  MIDGARD_SETTLEMENTS.filter(s => s.regionId === regionId);

// Get a specific settlement by ID
export const getSettlement = (settlementId) => 
  MIDGARD_SETTLEMENTS.find(s => s.id === settlementId);

// Get all locations in a settlement
export const getSettlementLocations = (settlementId) => 
  SETTLEMENT_LOCATIONS[settlementId] || [];

// Get a specific location by ID
export const getLocation = (settlementId, locationId) => {
  const locations = SETTLEMENT_LOCATIONS[settlementId] || [];
  return locations.find(l => l.id === locationId);
};

// Get unlocked settlements
export const getUnlockedSettlements = () => 
  MIDGARD_SETTLEMENTS.filter(s => s.unlocked);

// Get the starter town
export const getStarterTown = () => 
  MIDGARD_SETTLEMENTS.find(s => s.isStarterTown);
